// EXPECTING — Before baby arrives
// Deep editorial guide. All copy written to be educational, non-medical,
// and free of outcome promises.

export const expecting = {
  id: "expecting",
  num: "01",
  unit: "Before baby arrives",
  name: "Expecting",
  img: "/img-expecting.webp",
  alt: "A folded cream onesie and tiny booties waiting on rumpled linen bedding",
  titleLines: [
    { t: "You don\u2019t have to wait" },
    { t: "until you\u2019re exhausted" },
    { t: "to start understanding", em: true },
    { t: "newborn sleep.", em: true },
  ],
  introParas: [
    "You prepare for feeding, diapers, clothing, car seats, the nursery. Sleep can stay abstract until the baby actually arrives.",
    "Then suddenly you\u2019re trying to understand feeding, waking, naps, settling, day and night, safe sleep and whether anything you\u2019re seeing is normal — while already tired.",
    "This guide is realistic context, before that happens.",
  ],
  topics: [
    {
      id: "what-newborn-sleep-looks-like",
      q: "What does newborn sleep actually look like?",
      body: [
        "Short answer: fragmented. Newborn sleep commonly happens in stretches measured in hours rather than anything resembling a night, spread across both day and night, with waking driven heavily by feeding and comfort.",
        "That fragmentation isn\u2019t a malfunction. In the early weeks, sleep is organized around a brand-new nervous system, a tiny stomach, and rhythms that haven\u2019t settled into a day/night pattern yet. Two newborns of the same age can look completely different — and the same newborn can look different from one day to the next.",
        "This is why the first weeks rarely resemble the tidy schedules you see online. What you\u2019re watching is a pattern in progress, not a predictable schedule. A pattern is a loose shape — baby tends to feed, stay briefly awake, then sleep again. A schedule is events at fixed times. Newborns have the first long before they have the second.",
        "The patterns also move quickly. What you see at two weeks is not what you\u2019ll see at eight weeks. Understanding that sleep evolves fast helps you read change as development rather than as something going wrong.",
      ],
      observe: [
        "How long your baby typically stays asleep between feeds",
        "Whether some stretches of the day look different from others",
        "What seems to help your baby settle, and what doesn\u2019t",
      ],
    },
    {
      id: "day-and-night",
      q: "Will my newborn know the difference between day and night?",
      body: [
        "Not at first — and that\u2019s biology, not something you caused. Newborns are born without a mature circadian rhythm, the internal timing system that tells older children and adults when to be awake and when to sleep. It develops gradually over the early months.",
        "In the meantime, the world around the baby can quietly teach. Normal light and household activity during the day, and a dimmer, calmer, less interesting environment at night, give the developing rhythm something to organize around. Night feeds can be kept low-key: soft light, minimal interaction, back to sleep.",
        "What this isn\u2019t: a trick that makes a newborn sleep through the night. Environmental cues support a process that\u2019s already unfolding. The development itself takes time, and no routine can rush it.",
      ],
    },
    {
      id: "do-newborns-need-a-schedule",
      q: "Do newborns need a sleep schedule?",
      body: [
        "Not a clock-based one. Expecting a newborn to nap at 9:00 and 1:00 and sleep at 7:00 sharp sets a standard most newborns simply can\u2019t meet — and it turns normal variability into daily evidence that something is wrong.",
        "What is useful at this stage is observation. Over days, you may begin to notice your baby\u2019s own rhythm: roughly how long they can stay awake before unraveling, how feeding and sleep relate, what settling looks like for them, and how one day differs from another.",
        "That observation becomes the raw material for everything later. Parents who arrive at four months knowing their baby\u2019s patterns are in a completely different position than parents who spent those months chasing a timetable.",
      ],
      observe: [
        "Feeding — when, and how it relates to sleep",
        "Sleepy behavior — the cues your baby shows before sleep",
        "Waking — how long awake periods realistically last",
        "Settling — what helps, and how long it tends to take",
        "Sleep location — where sleep actually happens right now",
        "Day-to-day differences — how much variation is normal for your baby",
      ],
    },
    {
      id: "routine-vs-schedule",
      q: "What\u2019s the difference between a routine and a schedule?",
      body: [
        "Parents mix these up constantly, and the mix-up creates a lot of unnecessary pressure. A schedule says: events happen at specific times. A routine says: events happen in a familiar order.",
        "A newborn routine might be as simple as feed, diaper, dim lights, a quiet minute together, settling, sleep. The clock doesn\u2019t decide when it happens — the baby\u2019s cues do. The sequence is what makes it recognizable.",
        "Why it matters: routines can begin almost immediately, because they don\u2019t depend on timing. Predictable sequences give a baby consistent signals that sleep is coming, and they give you something steady to hold onto when the nights themselves are unpredictable.",
      ],
    },
    {
      id: "what-to-prepare",
      q: "What can we actually prepare before the baby arrives?",
      body: [
        "More than most parents expect. Not a schedule — but the conditions that make the early weeks easier to read.",
        "Start with the sleep environment: a flat, clear, safe sleep surface in a space you can reach at night, set up according to current safe-sleep guidance. Then think about cues — a few simple, repeatable signals (a darker room, a quieter voice, the same short sequence before sleep) that you can use from the first nights.",
        "The practical layer matters too. Know where the nighttime supplies are before 2 a.m. If there are two caregivers, talk now about how nights might be shared. And know the boundary line in advance: feeding, growth, breathing, lethargy, illness and anything that feels medically off belong with your pediatrician — sleep education sits alongside that care, not in place of it.",
      ],
      observe: [
        "Your pediatrician\u2019s preferred way to ask questions between visits",
        "Which caregiver handles which part of the night, if sharing is an option",
        "What \u2018normal newborn\u2019 actually looks like — before you\u2019re in it",
      ],
    },
    {
      id: "newborn-sleep-foundations",
      q: "What are \u201cnewborn sleep foundations\u201d?",
      body: [
        "The phrase gets used as marketing language, so let\u2019s make it concrete. Foundations are the small, repeatable things that make sleep easier to understand and support: a consistent settling sequence, an appropriate sleep environment, paying attention to your baby\u2019s cues, noticing patterns as they emerge, and holding expectations that match a newborn\u2019s development.",
        "Foundations are not sleep training. Formal sleep training approaches are designed for older infants. With a newborn, the work is understanding and gentle support — reading what your baby shows you, keeping sleep conditions consistent, and giving developing rhythms room to form.",
        "Parents who build this kind of literacy early tend to find the later stages less disorienting — because they already know their child\u2019s baseline when something changes.",
      ],
    },
    {
      id: "what-not-to-worry-about",
      q: "What should we not worry about yet?",
      body: [
        "The perfect schedule. The independent sleeper. The baby next door who supposedly sleeps through the night. None of those are the assignment in the first weeks.",
        "Understanding what\u2019s developmentally realistic does something powerful: it stops every unpredictable night from becoming evidence that you\u2019re doing something wrong. A rough night at three weeks is usually just a rough night at three weeks.",
        "None of this means dismissing real concerns. If something feels medically off, that\u2019s a pediatrician conversation — always. But ordinary newborn chaos, by itself, is not a verdict on your parenting.",
      ],
    },
    {
      id: "when-to-ask-pediatrician",
      q: "When should sleep questions go to the pediatrician?",
      body: [
        "A simple rule: anything involving health belongs with your child\u2019s healthcare professional. Feeding difficulties, slow growth, breathing concerns, unusual lethargy, illness, or anything that feels medically wrong — those are pediatrician questions, not sleep-consulting questions.",
        "Sleep education and support sit on the other side of that line: understanding patterns, shaping routines, setting expectations, and making sense of what you\u2019re seeing once health is established. When in doubt, ask the pediatrician first — that\u2019s what they\u2019re there for.",
      ],
    },
  ],
  checklist: {
    title: ["Before baby arrives,", "it helps to understand:"],
    items: [
      "what newborn sleep can realistically look like",
      "why sleep may happen in short stretches",
      "why days and nights may initially blur together",
      "routine versus rigid scheduling",
      "what early sleep foundations mean",
      "established safe-sleep basics",
      "when a sleep concern belongs with the pediatrician",
      "where professional newborn sleep support can fit",
    ],
    after: [
      { t: "None of this makes the nights shorter." },
      { t: "It makes them less confusing.", em: true },
    ],
  },
  ending: {
    lines: [
      { t: "You don\u2019t need to have" },
      { t: "newborn sleep mastered" },
      { t: "before your baby arrives.", em: true },
    ],
    sub: "But knowing what you\u2019re looking at can make those first weeks feel a lot less mysterious.",
  },
  ctas: [
    { type: "book", label: "Book my complimentary sleep assessment" },
    { type: "tell", label: "Expecting? Tell Andrea what you\u2019re preparing for" },
    { type: "text", label: "Text Andrea a question" },
  ],
};
